"use client";
import { DEFAULT_RANGE, datePickerFormatJP } from "@/constants";
import { ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel } from "@/types/generated";
import { ESubmitStatus } from "@/types/common";
import { format } from "date-fns/format";
import fuzzysort from "fuzzysort";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { Controller, useFormContext } from "react-hook-form";
import { IContactForm } from "./ContactFormModal";
import UploadAttachment from "./UploadAttachment";
import { Label } from "@/components/atoms/label";
import { Input } from "@/components/atoms/input";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icon";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Checkbox } from "@/components/atoms/checkbox";
import { TextArea } from "@/components/atoms/text-area";

interface IMeetingEventFormProps {
  formLabel: ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel;
  submitStatus?: ESubmitStatus;
}

/** Duration and Event type dropdown options */
const DURATION_OPTIONS = [
  { label: "終日", value: "終日" },
  { label: "午前", value: "午前" },
  { label: "午後", value: "午後" },
  { label: "夕方", value: "夕方" },
];

const EVENT_TYPE_OPTIONS = [
  { label: "パーティー", value: "パーティー" },
  { label: "会議 / セミナー", value: "会議 / セミナー" },
  { label: "カンファレンス", value: "カンファレンス" },
  { label: "展示会", value: "展示会" },
  { label: "仏教の法要", value: "仏教の法要" },
  { label: "宿泊", value: "宿泊" },
  { label: "その他", value: "その他" },
];
/**
 * MeetingEventForm Component
 *
 * Handles event and meeting details input including event type, date range, attendee count, and preferences.
 * Integrates React Hook Form for controlled form state management.
 * Includes date picker, dynamic select fields, file upload, and checkboxes for food options.
 */
const MeetingEventForm = ({
  formLabel,
  submitStatus,
}: IMeetingEventFormProps) => {
  const {
    meeting_event_information_text,
    event_meeting_name_label,
    nr_attendees_label,
    start_date_label,
    end_date_label,
    guest_rooms_label,
    duration_label,
    check_applicable_food_text,
    breakfast_label,
    lunch_label,
    dinner_label,
    reception_label,
    other_label,
    please_tell_us_text,
    attach_file_label,
    type_of_event_label,
  } = formLabel || {};
  const { control, setValue, formState } = useFormContext<IContactForm>();
  const { errors, isSubmitted } = formState;

  /** Manage currently selected date range for event period */

  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [previousRange, setPreviousRange] = useState<DateRange>(DEFAULT_RANGE);
  const [displayReset, setDisplayReset] = useState(false);

  /** Handle selection of start and end dates in the range picker */
  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    setDisplayReset(false);
    // Preserve previous full range for the Cancel action
    if (range.from && range.to) {
      setPreviousRange({ from: range.from, to: range.to });
    }

    // If picker cleared the range or no day selected, reset
    if (!nextRange || !selectedDay) {
      setRange(nextRange || DEFAULT_RANGE);
      setValue("startDate", nextRange?.from ?? null);
      setValue("endDate", nextRange?.to ?? null);
      return;
    }

    setRange((currentRange) => {
      // 1) If user clicks the same start date again, collapse to single-day range
      if (
        currentRange.from &&
        !currentRange.to &&
        currentRange.from.toDateString() === selectedDay.toDateString()
      ) {
        const singleDay: DateRange = { from: selectedDay, to: selectedDay };
        setValue("startDate", singleDay.from!);
        setValue("endDate", singleDay.to!);
        return singleDay;
      }

      // 2) If a full range exists and user clicks a DIFFERENT day,
      //    start a new range from that clicked day (do NOT extend)
      if (
        currentRange.from &&
        currentRange.to &&
        currentRange.from.toDateString() !== selectedDay.toDateString() &&
        currentRange.to.toDateString() !== selectedDay.toDateString()
      ) {
        const newRange: DateRange = { from: selectedDay, to: undefined };
        setValue("startDate", selectedDay);
        setValue("endDate", null);
        return newRange;
      }

      // 3) Otherwise, keep the picker-provided range (normal multi-day behavior)
      if (nextRange.from) {
        setValue("startDate", nextRange.from);
      } else {
        setValue("startDate", null);
      }
      if (nextRange.to) {
        setValue("endDate", nextRange.to);
      } else {
        setValue("endDate", null);
      }

      return nextRange;
    });
  };
  useEffect(() => {
    if (range.from && range.to) {
      setValue("startDate", range.from);
      setValue("endDate", range.to);
    }
  }, [range, setValue]);
  useEffect(() => {
    if (isSubmitted) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayReset(true); // show DEFAULT_RANGE display after submit
    }
  }, [isSubmitted]);

  const rangeToDisplay = displayReset ? DEFAULT_RANGE : range;

  return (
    <div className="w-full pt-3 md:pt-6">
      <div className="mb-2.5 text-center">
        <h3 className="text-xl font-[var(--font-semibold)] text-[var(--text-base-primary)]">
          {meeting_event_information_text || ""}
        </h3>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {event_meeting_name_label || ""}
            </Label>
            <Controller
              name="eventName"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors?.eventName?.message}
                  size="sm"
                />
              )}
            />
          </div>
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {nr_attendees_label || ""}
            </Label>
            <Controller
              name="noOfAttendees"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors?.noOfAttendees?.message}
                  size="sm"
                />
              )}
            />
          </div>
        </div>
        {/* Date range, rooms, and duration */}
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="flex-1">
            <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
              {start_date_label || ""} - {end_date_label || ""}
            </Label>
            <DateRangePicker
              range={rangeToDisplay}
              onChange={handleRangeSelect}
              onCancel={() => {
                setRange(previousRange);
              }}
            >
              <Button
                hierarchy="outline"
                variant="neutral"
                className="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-4.5 text-sm font-medium text-[var(--text-gray-light)]"
              >
                <span>
                  {rangeToDisplay?.from
                    ? format(rangeToDisplay.from, datePickerFormatJP)
                    : "開始日"}
                </span>
                <ArrowRight
                  className="text-[var(--text-gray-light)]"
                  width={16}
                  height={16}
                />
                <span>
                  {rangeToDisplay?.to
                    ? format(rangeToDisplay.to, datePickerFormatJP)
                    : "終了日"}
                </span>
                <Icon icon={"calendar"} />
              </Button>
            </DateRangePicker>
          </div>

          {/* Number of guest rooms - restricts non-numeric input */}
          <div className="flex-1">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {guest_rooms_label || ""}
            </Label>
            <Controller
              name="rooms"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  type="number"
                  onChange={(e) => {
                    // Prevent negative numbers
                    const value = e.target.value;
                    if (!value || /^\d*$/.test(value)) {
                      field.onChange(value);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent typing "-" or "e" in the input
                    if (e.key === "-" || e.key === "e" || e.key === "E") {
                      e.preventDefault();
                    }
                  }}
                  size="sm"
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {duration_label || ""}
            </Label>
            <Controller
              name="duration"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  value={field.value}
                  options={DURATION_OPTIONS}
                  onChange={field.onChange}
                  className="w-full"
                  placeholder=""
                  size="sm"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    const result = fuzzysort.single(
                      inputValue,
                      option.data.label,
                    );
                    return result !== null;
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      paddingLeft: "12px",
                      paddingRight: "12px",
                    }),
                    input: (base) => ({
                      ...base,
                      fontSize: "16px",
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "var(--text-gray-stronger)",
                      padding: "8px 12px",
                    }),
                  }}
                />
              )}
            />
          </div>
        </div>

        <div>
          <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
            {type_of_event_label || ""}
          </Label>
          <Controller
            name="typeOfEvent"
            control={control}
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                options={EVENT_TYPE_OPTIONS}
                onChange={field.onChange}
                className="w-full"
                placeholder=""
                size="sm"
                filterOption={(option, inputValue) => {
                  if (!inputValue) return true;
                  const result = fuzzysort.single(
                    inputValue,
                    option.data.label,
                  );
                  return result !== null;
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    paddingLeft: "12px",
                    paddingRight: "12px",
                  }),
                  input: (base) => ({
                    ...base,
                    fontSize: "16px",
                  }),
                  groupHeading: (base) => ({
                    ...base,
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "var(--text-gray-stronger)",
                    padding: "8px 12px",
                  }),
                }}
              />
            )}
          />
        </div>

        <div className="font-medium text-[var(--text-gray-normal)]">
          <Label className="mb-1.5">{check_applicable_food_text || ""}</Label>

          <div className="flex flex-wrap gap-4">
            <Controller
              name="hasBreakfast"
              control={control}
              render={({ field }) => (
                <Checkbox
                  onCheckedChange={field.onChange}
                  label={breakfast_label || ""}
                  size="sm"
                />
              )}
            />
            <Controller
              name="hasLunch"
              control={control}
              render={({ field }) => (
                <Checkbox
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  label={lunch_label || ""}
                  size="sm"
                />
              )}
            />
            <Controller
              name="hasDinner"
              control={control}
              render={({ field }) => (
                <Checkbox
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  label={dinner_label || ""}
                  size="sm"
                />
              )}
            />
            <Controller
              name="hasReception"
              control={control}
              render={({ field }) => (
                <Checkbox
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  label={reception_label || ""}
                  size="sm"
                />
              )}
            />
            <Controller
              name="hasOther"
              control={control}
              render={({ field }) => (
                <Checkbox
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  label={other_label || ""}
                  size="sm"
                />
              )}
            />
          </div>
        </div>

        <div className="w-full">
          <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
            {please_tell_us_text || ""}
          </Label>
          <Controller
            name="requirement"
            control={control}
            render={({ field }) => (
              <TextArea
                className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                value={field.value ?? ""}
                onChange={field.onChange}
                size="sm"
              />
            )}
          />
        </div>

        <div className="w-full">
          <Controller
            name="attachFiles"
            control={control}
            render={({ field }) => (
              <UploadAttachment
                label={attach_file_label || ""}
                onChange={field.onChange}
                submitStatus={submitStatus}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default MeetingEventForm;
